import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './ConversationPage.css';

/**
 * Conversation page using OpenAI Realtime API
 * Connects to backend WebSocket proxy which forwards to OpenAI Realtime API
 */
function ConversationPageRealtime({ apiUrl }) {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [status, setStatus] = useState('idle'); // idle, connecting, listening, thinking, speaking
  const [transcript, setTranscript] = useState({ user: '', assistant: '' });
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [disclosureAccepted, setDisclosureAccepted] = useState(false);
  const [disclosureText, setDisclosureText] = useState(
    'This is an AI companion that simulates your family member\'s voice and manner. ' +
    'It is not the actual person. Do not rely on it for medical, legal, or emergency advice.'
  );
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioProcessorRef = useRef(null);

  useEffect(() => {
    const key = 'aiko_disclosure_v1';
    if (localStorage.getItem(key) === 'true') {
      setDisclosureAccepted(true);
    }
    axios.get(`${apiUrl}/config/disclosure`)
      .then((response) => {
        if (response.data?.disclosure) {
          setDisclosureText(response.data.disclosure);
        }
      })
      .catch(() => {});
  }, [apiUrl]);

  const acceptDisclosure = async () => {
    try {
      await axios.post(`${apiUrl}/consent/accept`, {
        consent_type: 'ai_disclosure',
        profile_id: profileId,
      });
    } catch (err) {
      console.warn('Consent recording failed (continuing):', err);
    }
    localStorage.setItem('aiko_disclosure_v1', 'true');
    setDisclosureAccepted(true);
  };

  useEffect(() => {
    if (!disclosureAccepted || !token) return;

    let mounted = true;
    
    const setup = async () => {
      try {
        // Initialize audio context for playback
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        
        // Get WebSocket URL (backend proxy through Caddy)
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = window.location.hostname;
        const wsPort = window.location.port ? `:${window.location.port}` : '';
        // WebSocket goes through Caddy at /ws/realtime/:profileId
        // Include authentication token in query params
        const wsUrl = `${wsProtocol}//${wsHost}${wsPort}/ws/realtime/${profileId}?token=${encodeURIComponent(token || '')}`;
        
        console.log('Connecting to Realtime WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = async () => {
          console.log('Realtime WebSocket connected');
          setConnected(true);
          setError(null);
          
          // Set binary type for audio data
          ws.binaryType = 'arraybuffer';
          
          // Resume audio context if suspended (required for autoplay policies)
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            console.log('⏸️ AudioContext suspended on connect, resuming...');
            try {
              await audioContextRef.current.resume();
              console.log('✅ AudioContext resumed');
            } catch (err) {
              console.error('❌ Error resuming AudioContext:', err);
            }
          }
          
          setStatus('listening');
          
          // Start microphone capture
          startMicrophoneCapture();
        };
        
        ws.onmessage = async (event) => {
          try {
            // Check if it's binary audio data (from TTS service)
            if (event.data instanceof Blob || event.data instanceof ArrayBuffer) {
              console.log('Received audio data from TTS service');
              await playAudioBlob(event.data);
              setStatus('speaking');
              return;
            }
            
            // Parse JSON message
            const message = JSON.parse(event.data);
            console.log('Realtime message:', message);
            
            // Handle different message types
            if (message.type === 'transcript') {
              // Transcript update (user or assistant)
              setTranscript(prev => ({
                user: message.user || prev.user,
                assistant: message.assistant || prev.assistant
              }));
              if (message.user) {
                setStatus('thinking');
              }
            } else if (message.type === 'audio_ready') {
              // Audio synthesis complete
              setStatus('listening');
            } else if (message.type === 'conversation.item.input_audio_buffer.committed') {
              // User speech committed
              if (message.item?.transcript) {
                setTranscript(prev => ({
                  ...prev,
                  user: message.item.transcript
                }));
              }
              setStatus('thinking');
            } else if (message.type === 'conversation.item.input_audio_buffer.speech_started') {
              setStatus('listening');
            } else if (message.type === 'conversation.item.input_audio_buffer.speech_stopped') {
              setStatus('thinking');
            } else if (message.type === 'session.created' || message.type === 'session.updated') {
              console.log('Session event:', message);
            } else if (message.type === 'error') {
              console.error('Realtime API error:', message);
              let errorMsg = message.message || message.error?.message || 'Unknown error';
              
              // Handle specific error types
              if (message.error_type === 'insufficient_quota') {
                errorMsg = 'OpenAI API quota exceeded. Please check your API key and billing settings.';
              }
              
              setError(errorMsg);
              setStatus('error');
            }
          } catch (err) {
            console.error('Error handling message:', err);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setError('Connection error occurred');
          setConnected(false);
        };
        
        ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          setConnected(false);
          setStatus('idle');
          if (event.code !== 1000 && event.code !== 1001) {
            setError(`Connection closed: ${event.reason || 'Unknown reason'}`);
          }
        };
        
      } catch (err) {
        console.error('Setup error:', err);
        if (mounted) {
          setError('Failed to initialize: ' + err.message);
        }
      }
    };
    
    setup();
    
    return () => {
      mounted = false;
      cleanup();
    };
  }, [profileId, token, disclosureAccepted]);

  const startMicrophoneCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000, // OpenAI Realtime API uses 24kHz
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      mediaStreamRef.current = stream;
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
      
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      let audioChunkCount = 0;
      let lastLogTime = Date.now();
      processor.onaudioprocess = (e) => {
        // Check if WebSocket is open and connected
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Convert Float32Array to Int16Array (PCM16)
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            int16Data[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }
          
          // Send audio as binary data (backend will convert to base64)
          try {
            wsRef.current.send(int16Data.buffer);
            audioChunkCount++;
            
            // Log every 5 seconds
            const now = Date.now();
            if (now - lastLogTime > 5000) {
              console.log(`🎤 Sent ${audioChunkCount} audio chunks (${int16Data.length} samples each)`);
              lastLogTime = now;
            }
          } catch (err) {
            console.error('Error sending audio:', err);
          }
        } else {
          // WebSocket not ready - log occasionally
          const now = Date.now();
          if (now - lastLogTime > 5000) {
            console.warn(`⚠️ WebSocket not ready. State: ${wsRef.current?.readyState}, Connected: ${connected}`);
            lastLogTime = now;
          }
        }
      };
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      audioProcessorRef.current = processor;
      
      console.log('Microphone capture started');
    } catch (err) {
      console.error('Error starting microphone:', err);
      setError('Failed to access microphone: ' + err.message);
    }
  };

  const playAudioBlob = async (audioBlob) => {
    try {
      // TTS service sends audio as WAV file (Blob or ArrayBuffer)
      const audioContext = audioContextRef.current;
      if (!audioContext) {
        console.error('❌ AudioContext is null!');
        return;
      }
      
      // Resume audio context if suspended (required for autoplay policies)
      if (audioContext.state === 'suspended') {
        console.log('⏸️ AudioContext suspended, resuming...');
        await audioContext.resume();
        console.log('✅ AudioContext resumed');
      }
      
      let arrayBuffer;
      if (audioBlob instanceof Blob) {
        arrayBuffer = await audioBlob.arrayBuffer();
        console.log(`📦 Received Blob, size: ${audioBlob.size} bytes`);
      } else if (audioBlob instanceof ArrayBuffer) {
        arrayBuffer = audioBlob;
        console.log(`📦 Received ArrayBuffer, size: ${arrayBuffer.byteLength} bytes`);
      } else {
        console.error('❌ Unknown audio data type:', typeof audioBlob, audioBlob);
        return;
      }
      
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        console.error('❌ Empty audio buffer!');
        return;
      }
      
      console.log(`🎵 Decoding audio buffer: ${arrayBuffer.byteLength} bytes`);
      
      // Decode and play audio (WAV format from TTS service)
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      console.log(`✅ Audio decoded: ${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.sampleRate}Hz, ${audioBuffer.numberOfChannels} channels`);
      
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      
      console.log('🔊 Starting audio playback...');
      source.start();
      
      // Update status when audio finishes
      source.onended = () => {
        console.log('✅ Audio playback finished');
        setStatus('listening');
      };
    } catch (err) {
      console.error('❌ Error playing audio:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setStatus('listening'); // Reset status on error
    }
  };

  const cleanup = () => {
    console.log('Cleaning up...');
    
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    if (audioProcessorRef.current) {
      try {
        audioProcessorRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      audioProcessorRef.current = null;
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Already closed or closing
      }
      audioContextRef.current = null;
    }
    
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close(1000, 'Cleanup');
      wsRef.current = null;
    }
  };

  const handleEndConversation = () => {
    cleanup();
    navigate(user?.user_role === 'elderly' ? '/home' : '/personalization');
  };

  return (
    <div className="conversation-page">
      {!disclosureAccepted && (
        <div className="disclosure-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '32px', borderRadius: '12px',
            maxWidth: '520px', margin: '20px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ marginTop: 0 }}>Before You Begin</h2>
            <p style={{ fontSize: '18px', lineHeight: 1.5 }}>{disclosureText}</p>
            <button
              onClick={acceptDisclosure}
              className="btn btn-primary"
              style={{ marginTop: '16px', width: '100%', padding: '14px', fontSize: '18px' }}
            >
              I Understand — Start Conversation
            </button>
          </div>
        </div>
      )}
      <div className="container">
        <h1>Voice Conversation</h1>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="status-display">
          <span className={`status-indicator status-${status}`}></span>
          <span className="status-text">
            {status === 'idle' && 'Ready to start'}
            {status === 'connecting' && 'Connecting...'}
            {status === 'listening' && 'Listening...'}
            {status === 'thinking' && 'Thinking...'}
            {status === 'speaking' && 'Speaking...'}
          </span>
        </div>
        
        {!connected && status === 'idle' && (
          <div className="info-message">
            <p>Start speaking to begin the conversation...</p>
          </div>
        )}
        
        {transcript.user && (
          <div className="transcript-section">
            <div className="transcript-item">
              <strong>You:</strong> {transcript.user}
            </div>
            {transcript.assistant && (
              <div className="transcript-item">
                <strong>Assistant:</strong> {transcript.assistant}
              </div>
            )}
          </div>
        )}
        
        <div className="controls">
          <button 
            onClick={handleEndConversation}
            className="btn btn-secondary"
          >
            End Conversation
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConversationPageRealtime;
